import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { Router } from '@angular/router';
import { catchError, of, takeUntil } from 'rxjs';
import { driver } from 'driver.js';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import {
  CompanyDetails,
  Properties,
} from '../../../../shared/models/company-details';
import {
  Company,
  ContainerSasUrl,
} from '../../../../shared/interfaces/company';
import { formatAddress } from '../../../../shared/utils/utils';
import { RecordDetailsComponent } from '../../../../shared/components/record-details/record-details.component';
import { ImageService } from '../../../../shared/services/image-service/image.service';
import { SkeletonComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { SMEService } from '../../services/sme.service';
import { CreateProfileWizardComponent } from '../create-profile-wizard/create-profile-wizard.component';
import { BusinessInfoEditComponent } from '../business-info-edit/business-info-edit.component';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { BusinessDocumentsComponent } from '../business-documents/business-documents.component';

@Component({
  selector: 'app-business-profiles',
  templateUrl: './business-profiles.component.html',
  styleUrls: ['./business-profiles.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CreateProfileWizardComponent,
    RecordDetailsComponent,
    SkeletonComponent,
    BusinessInfoEditComponent,
    PopupComponent,
    BusinessDocumentsComponent,
  ],
})
export class BusinessProfilesComponent
  extends MenuComponentBase
  implements OnInit
{
  private imageService = inject(ImageService);
  private smeService = inject(SMEService);
  private router = inject(Router);

  isLoading = false;
  hasError = false;
  /** true = no profile exists yet (GET returned 404) */
  hasNoProfile = false;

  businessProfile: CompanyDetails | null = null;
  containerSasUrl!: ContainerSasUrl;

  isRecordDetailsPopupOpen = false;
  showCreateWizard = false;
  isEditingInfo = false;
  interestedProducts: string[] = [];

  /** Products section edit mode */
  isEditingProducts = false;
  /** The display name of the product pending deletion */
  productToDelete: string | null = null;
  /** Whether a delete API call is in progress */
  isDeletingProduct = false;

  /** Whether the add-product picker panel is open */
  showAddPanel = false;
  /** Whether an add-product API call is in progress */
  isAddingProduct = false;
  /** List of products selected in the multi-select add panel */
  selectedProductsToAdd: string[] = [];

  /** Whether the delete-business confirmation popup is shown */
  showDeleteBusinessPopup = false;
  /** Whether the delete-business API call is in progress */
  isDeletingBusiness = false;

  /** Whether the Chamber of Commerce info popup is shown */
  showChamberPopup = false;

  /** Full options map: display name → option key (populated on profile load) */
  interestedProductOptions: Record<string, string> = {};

  @ViewChild('summaryEl') summaryEl!: ElementRef<HTMLDivElement>;
  isSummaryExpanded = false;
  showSummaryToggle = false;

  /** Tooltip state (matches CDFI list pattern) */
  activeTooltip: string | null = null;

  /** Whether the assignees detail popup is shown */
  showAssigneesPopup = false;

  override ngOnInit(): void {
    super.ngOnInit();

    this.imageService
      .getContainerSasUrl()
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => {
        this.containerSasUrl = url;
      });

    this.loadBusinessProfile();
  }

  loadBusinessProfile(): void {
    this.isLoading = true;
    this.hasError = false;
    this.hasNoProfile = false;
    this.businessProfile = null;

    this.smeService
      .getSMEBusinessRecord()
      .pipe(
        takeUntil(this.destroy$),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404) {
            this.hasNoProfile = true;
          } else {
            this.hasError = true;
            console.error('Error loading business profile', err);
          }
          return of(null);
        }),
      )
      .subscribe((profile) => {
        this.isLoading = false;
        if (profile) {
          this.businessProfile = profile;
          this.extractInterestedProducts();
          this.checkSummaryToggle();
        }
      });
  }

  openProfile(): void {
    if (this.businessProfile) {
      this.isRecordDetailsPopupOpen = true;
    }
  }

  getImageUrl(path: string): string {
    if (!this.containerSasUrl || !path) return '';
    return `${path}?${this.containerSasUrl.sas_token}`;
  }

  closeRecordDetailsPopup(): void {
    this.isRecordDetailsPopupOpen = false;
  }

  onCompanyDeleted(): void {
    this.isRecordDetailsPopupOpen = false;
    this.businessProfile = null;
    this.hasNoProfile = true;
  }

  onCompanyDetailsUpdated(updatedCompany: Company): void {
    // Reload to get the freshest data after an update
    this.isRecordDetailsPopupOpen = false;
    this.loadBusinessProfile();
    void updatedCompany; // suppress unused warning
  }

  openCreateWizard(): void {
    this.showCreateWizard = true;
  }

  closeCreateWizard(): void {
    this.showCreateWizard = false;
  }

  onProfileCreated(profile?: CompanyDetails): void {
    this.showCreateWizard = false;
    if (profile) {
      this.businessProfile = profile;
      this.hasNoProfile = false;
      this.extractInterestedProducts();
      this.checkSummaryToggle();
      this.triggerPostCreationHighlight();
    } else {
      this.loadBusinessProfile();
      this.triggerPostCreationHighlight();
    }
  }

  getProfileDisplayName(profile: CompanyDetails): string {
    if (!profile?.detail?.properties) return 'Business Profile';
    const nameProp = profile.detail.properties.find(
      (p) => p.property?.toLowerCase() === 'company_name',
    );
    return nameProp?.value || 'Business Profile';
  }

  getFormatAddress(address: string): string {
    return formatAddress(address);
  }

  getAssigneeBadges(company: CompanyDetails): {
    initial: string;
    name: string;
    cdfi_logo_path: string | null;
  }[] {
    const assignees = company.assignees || [];
    const maxVisible = 3;

    const allBadges = assignees.map((assignee) => {
      const connectionName =
        assignee.display_name || assignee.connection_name || '';
      const initial = connectionName.charAt(0).toUpperCase();
      let logo_url: string | null = null;
      if (assignee.cdfi_logo_path && this.containerSasUrl) {
        logo_url =
          assignee.cdfi_logo_path + `?${this.containerSasUrl.sas_token}`;
      }
      return { initial, name: connectionName, cdfi_logo_path: logo_url };
    });

    return allBadges.slice(0, maxVisible);
  }

  getExtraAssigneesCount(company: CompanyDetails): number {
    const assignees = company.assignees || [];
    const maxVisible = 3;
    return Math.max(0, assignees.length - maxVisible);
  }

  /** Cast CompanyDetails to Company so record-details popup can accept it */
  get companyForPopup(): Company {
    return this.businessProfile as unknown as Company;
  }

  extractInterestedProducts(): void {
    if (!this.businessProfile?.detail?.properties) {
      this.interestedProducts = [];
      this.interestedProductOptions = {};
      return;
    }

    const interestedProductsProp = this.businessProfile.detail.properties.find(
      (p) => p.property?.toLowerCase() === 'interested_products',
    );

    // Build full options map: display name → key
    this.interestedProductOptions = {};
    const rawOptions = interestedProductsProp?.options ?? {};
    for (const [k, v] of Object.entries(rawOptions)) {
      if (typeof v === 'string') {
        this.interestedProductOptions[k] = v;
      }
    }

    const rawVal = interestedProductsProp?.value;
    if (Array.isArray(rawVal)) {
      this.interestedProducts = rawVal.filter(
        (item): item is string => typeof item === 'string',
      );
    } else {
      this.interestedProducts = [];
    }
  }

  /** Products not yet selected by the user */
  get availableProducts(): string[] {
    return Object.keys(this.interestedProductOptions).filter(
      (name) => !this.interestedProducts.includes(name),
    );
  }

  get isHasBusinessNotYes(): boolean {
    if (!this.businessProfile?.detail?.properties) return false;
    const prop = this.businessProfile.detail.properties.find(
      (p) => p.property === 'has_business',
    );
    const val = prop?.value ? String(prop.value).toLowerCase() : '';
    return val !== 'yes';
  }

  get basicInformationProperties(): Properties[] {
    if (!this.businessProfile?.detail?.properties) return [];
    return this.businessProfile.detail.properties.filter(
      (p) =>
        p.group === 'Basic Information' &&
        p.property !== 'interested_products' &&
        p.property !== 'application_summary',
    );
  }

  get applicationSummaryProperty(): Properties | null {
    if (!this.businessProfile?.detail?.properties) return null;
    return (
      this.businessProfile.detail.properties.find(
        (p) => p.property === 'application_summary',
      ) ?? null
    );
  }

  get applicationSummaryHtml(): string {
    const val = this.applicationSummaryProperty?.value;
    if (val === null || val === undefined || val === '') return '';
    const text = String(val);
    if (text.includes('<p>') || text.includes('<br>')) {
      return text;
    }
    return text.replace(/\n/g, '<br>');
  }

  checkSummaryToggle(): void {
    this.isSummaryExpanded = false;
    this.showSummaryToggle = false;
    setTimeout(() => {
      if (this.summaryEl) {
        const el = this.summaryEl.nativeElement;
        this.showSummaryToggle = el.scrollHeight > 120;
      }
    }, 150);
  }

  toggleSummary(): void {
    this.isSummaryExpanded = !this.isSummaryExpanded;
  }

  // ── Tooltip helpers (matching CDFI list pattern) ─────────────────────────

  toggleTooltip(id: string, event: Event): void {
    event.stopPropagation();
    this.activeTooltip = this.activeTooltip === id ? null : id;
  }

  closeAllTooltips(): void {
    this.activeTooltip = null;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeAllTooltips();
  }

  // ── Lead status label lookup ──────────────────────────────────────────────

  getLeadStatusLabel(statusCode: number | undefined | null): string {
    if (statusCode === null || statusCode === undefined) return 'No status';
    const statusMap: Record<number, string> = {
      0: 'Select Lead Status',
      1: 'Under review',
      2: 'Further information required',
      3: 'Prospective client',
      4: 'On hold',
      5: 'Declined',
      6: 'Not currently pursuing',
      7: 'Active client',
      8: 'Request lead contact',
      9: 'Active Funded',
      10: 'Active Funded Grant',
      11: 'Active Technical Assistance',
    };
    return statusMap[statusCode] ?? `Status ${statusCode}`;
  }

  // ── CDFI profile navigation ───────────────────────────────────────────────

  navigateToCdfiProfile(connectionName: string): void {
    if (!connectionName) return;
    void this.router.navigate(['/cdfi/profile', connectionName]);
  }

  navigateToMatches(): void {
    void this.router.navigate(['/matches']);
  }

  formatPropertyValue(prop: Properties): string {
    const val: unknown = prop.value;
    if (val === null || val === undefined || val === '') return '-';
    if (Array.isArray(val)) {
      return val.length === 0 ? '-' : val.join(', ');
    }
    return String(val);
  }

  navigateToEditInfo(): void {
    this.isEditingInfo = true;
  }

  onEditSaved(profile?: CompanyDetails): void {
    this.isEditingInfo = false;
    if (profile) {
      this.businessProfile = profile;
      this.hasNoProfile = false;
      this.extractInterestedProducts();
      this.checkSummaryToggle();
    } else {
      this.loadBusinessProfile();
    }
  }

  toggleProductsEdit(): void {
    this.isEditingProducts = !this.isEditingProducts;
    this.productToDelete = null;
    this.showAddPanel = false;
  }

  openAddPanel(): void {
    this.selectedProductsToAdd = [];
    this.showAddPanel = true;
  }

  closeAddPanel(): void {
    this.showAddPanel = false;
    this.selectedProductsToAdd = [];
  }

  isProductSelected(name: string): boolean {
    return this.selectedProductsToAdd.includes(name);
  }

  toggleProductSelection(name: string): void {
    const idx = this.selectedProductsToAdd.indexOf(name);
    if (idx > -1) {
      this.selectedProductsToAdd.splice(idx, 1);
    } else {
      this.selectedProductsToAdd.push(name);
    }
  }

  addSelectedProducts(): void {
    if (this.isAddingProduct || this.selectedProductsToAdd.length === 0) {
      return;
    }

    // Current keys from the existing selected products
    const currentKeys: string[] = this.interestedProducts.map(
      (name) => this.interestedProductOptions[name] ?? name,
    );

    // New keys to add
    const newKeys = this.selectedProductsToAdd
      .map((name) => this.interestedProductOptions[name])
      .filter((key): key is string => typeof key === 'string');

    const updatedKeys = [...currentKeys, ...newKeys];

    const payload = {
      properties: [{ property: 'interested_products', value: updatedKeys }],
    };

    this.isAddingProduct = true;
    this.smeService
      .updateSMEBusinessRecord(payload)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err: HttpErrorResponse) => {
          console.error('Error adding products', err);
          return of(null);
        }),
      )
      .subscribe((result) => {
        this.isAddingProduct = false;
        this.showAddPanel = false;
        this.selectedProductsToAdd = [];
        if (result) {
          this.businessProfile = result;
          this.extractInterestedProducts();
          this.checkSummaryToggle();
        }
      });
  }

  confirmDeleteProduct(event: Event, productDisplayName: string): void {
    event.stopPropagation();
    this.productToDelete = productDisplayName;
  }

  cancelDeleteProduct(): void {
    this.productToDelete = null;
  }

  deleteProduct(): void {
    if (!this.productToDelete || !this.businessProfile?.detail?.properties) {
      return;
    }

    const allProps = this.businessProfile.detail.properties;

    // Resolve the option key for the product being removed
    const interestedProp = allProps.find(
      (p) => p.property === 'interested_products',
    );
    const rawOptions = interestedProp?.options ?? {};
    // Build a string→string map of display name → option key (no any casts)
    const options: Record<string, string> = {};
    for (const [k, v] of Object.entries(rawOptions)) {
      if (typeof v === 'string') {
        options[k] = v;
      }
    }
    const removedKey = options[this.productToDelete] ?? '';

    // Build the new interested_products value (array of remaining keys)
    const rawValue = interestedProp?.value;
    const valueArray: string[] = Array.isArray(rawValue)
      ? (rawValue as string[]).filter(
          (item): item is string => typeof item === 'string',
        )
      : [];
    const currentKeys = valueArray
      .map((displayName) => options[displayName] ?? displayName)
      .filter((key) => key !== removedKey);

    const payload: { properties: { property: string; value: unknown }[] } = {
      properties: [{ property: 'interested_products', value: currentKeys }],
    };

    // Clear all properties whose prerequisite.value is the removed product key
    for (const prop of allProps) {
      if (
        prop.prerequisite &&
        prop.prerequisite.property === 'interested_products' &&
        prop.prerequisite.value === removedKey
      ) {
        payload.properties.push({ property: prop.property, value: null });
      }
    }

    this.isDeletingProduct = true;
    this.smeService
      .updateSMEBusinessRecord(payload)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err: HttpErrorResponse) => {
          console.error('Error deleting product', err);
          return of(null);
        }),
      )
      .subscribe((result) => {
        this.isDeletingProduct = false;
        this.productToDelete = null;
        if (result) {
          this.businessProfile = result;
          this.extractInterestedProducts();
          this.checkSummaryToggle();
        }
      });
  }

  openProductDetails(productName: string): void {
    const route = this.getProductRoute(productName);
    if (route) {
      this.router.navigate([route], {
        state: { businessProfile: this.businessProfile },
      });
    } else {
      const name = productName.toLowerCase();
      if (name.includes('chamber') || name.includes('networking')) {
        this.showChamberPopup = true;
      }
    }
  }

  isChamberOrNetworking(productName: string): boolean {
    const name = productName.toLowerCase();
    return name.includes('chamber') || name.includes('networking');
  }

  getProductRoute(productName: string): string | null {
    const name = productName.toLowerCase();

    if (name.includes('loan') || name.includes('capital')) {
      return '/business/loans-capital';
    }
    if (name.includes('technical') || name.includes('assistance')) {
      return '/business/technical-assistance';
    }
    if (name.includes('operational') || name.includes('tools')) {
      return '/business/operational-tools';
    }
    if (name.includes('real estate')) {
      return '/business/real-estate';
    }
    if (name.includes('solar') || name.includes('energy')) {
      return '/business/solar-energy';
    }
    if (name.includes('investor')) {
      return '/business/finding-investors';
    }

    return null;
  }

  getProductKey(productName: string): string {
    // Convert product name to a key format
    return productName.toLowerCase().replace(/\s+/g, '_');
  }

  confirmDeleteBusiness(): void {
    this.showDeleteBusinessPopup = true;
  }

  cancelDeleteBusiness(): void {
    this.showDeleteBusinessPopup = false;
  }

  deleteBusiness(): void {
    if (this.isDeletingBusiness) return;
    this.isDeletingBusiness = true;
    this.smeService
      .deleteSMEBusinessRecord()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isDeletingBusiness = false;
          this.showDeleteBusinessPopup = false;
          this.businessProfile = null;
          this.hasNoProfile = true;
          this.interestedProducts = [];
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error deleting business profile', err);
          this.isDeletingBusiness = false;
          this.showDeleteBusinessPopup = false;
        },
      });
  }

  getProductIcon(productName: string): string {
    const name = productName.toLowerCase();
    if (name.includes('loan') || name.includes('capital')) {
      return 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z';
    }
    if (name.includes('technical') || name.includes('assistance')) {
      return 'M19.43 12.98c.04-.32.07-.64.07-.98 0-.34-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.09-.16-.26-.25-.44-.25-.06 0-.12.01-.17.03l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.06-.02-.12-.03-.18-.03-.17 0-.34.09-.43.25l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98 0 .33.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.09.16.26.25.44.25.06 0 .12-.01.17-.03l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.06.02.12.03.18.03.17 0 .34-.09.43-.25l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z';
    }
    if (name.includes('operational') || name.includes('tools')) {
      return 'M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z';
    }
    if (name.includes('real estate')) {
      return 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z';
    }
    if (name.includes('solar') || name.includes('energy')) {
      return 'M20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69L23.31 12 20 8.69zM12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm0-10c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4z';
    }
    if (name.includes('investor')) {
      return 'M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z';
    }
    if (name.includes('chamber') || name.includes('networking')) {
      return 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z';
    }
    return 'M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z';
  }

  triggerPostCreationHighlight(): void {
    // Smoothly scroll down to the Products & Services section
    setTimeout(() => {
      const productsEl = document.getElementById('tour-products-services');
      if (productsEl) {
        productsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 350);

    // Launch the driver.js highlight spotlight once the scroll settles
    setTimeout(() => {
      this.startPostCreationTour();
    }, 750);
  }

  startPostCreationTour(): void {
    const driverObj = driver({
      showProgress: true,
      animate: true,
      popoverClass: 'driverjs-theme post-creation-highlight-tour',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Got It',
      showButtons: ['next', 'close'],
      steps: [
        {
          element: '#tour-products-services',
          popover: {
            title: 'Your Products & Services',
            description:
              'Your selected products and services are listed over here. Click each item to view and update details to assist you better.',
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#tour-business-documents',
          popover: {
            title: 'Business Documents',
            description:
              'Add any supporting documents here to assist organizations in evaluating your profile.',
            side: 'top',
            align: 'start',
          },
        },
      ],
    });

    driverObj.drive();
  }
}
